// @flow
/* eslint-disable @gandi/gandi/no-global-element */

import * as React from 'react';

import clsx from 'clsx';
import { connect } from 'react-redux';

import { Link } from 'react-router-dom';
import type { To } from 'react-router-dom';
import { compose } from 'redux';
import { superpathjoin } from 'superpathjoin';

import { serializeQuery } from '@gandi/app-runner';
import { selectCartUUID } from '@gandi/cart';
import { withTranslator, type TranslatorProps } from '@gandi/translate';
import { IconButton, Base, Svg } from '@gandi/ui';

import { withRouter } from 'src/helpers/withRouter';

import type { WithRouterProps, GetState } from '../../types';

import styles from './DomainInput.scss';

const bulkRowsDefaultNb = 7; /* most common use of bulk search is 4-7 rows */

type DomainProcess = 'create' | 'renew' | 'transfer';

type OwnProps = {
  hideOptions?: boolean,
  hideBulk?: boolean,
  placeholder?: string,
  label: string,
};

type Props = {
  translator: TranslatorProps,
  ...WithRouterProps<
    {
      cartId: string,
    },
    {
      search: string,
      options: '0' | '1',
      bulk: '0' | '1',
    },
  >,
  title: React.Node,
  id: string,
  onSearchSubmit?: () => void,

  submitSearch: string => mixed,

  cartId: string,
  value: string,
  loading: boolean,
  process: DomainProcess,

  children?: React.Node,
  bulk: boolean,
  options: boolean,
  ...OwnProps,
};

// only use state for vars that cannot be stored in query
type State = {
  bulkRows: number,
  value: string,
};

class DomainInput extends React.Component<Props, State> {
  input: {
    current: HTMLInputElement | HTMLTextAreaElement | null,
  };

  constructor(props: Props) {
    super(props);

    this.input = React.createRef();

    this.state = {
      value: props.value,
      bulkRows: bulkRowsDefaultNb,
    };
  }

  componentDidMount() {
    if (!this.props.value && this.input.current) {
      this.input.current.focus();
    }

    const bulk = this.props.value.split(' ').length > 1 || this.props.bulk;
    const shouldDisplayOptions =
      this.props.process === 'create' &&
      this.props.options &&
      !this.props.hideOptions;

    if (
      bulk !== this.props.bulk ||
      shouldDisplayOptions !== this.props.options
    ) {
      this.props.setSearchParams(prev => {
        prev.delete('bulk');
        prev.delete('options');

        if (bulk) {
          prev.set('bulk', '1');
        }
        if (shouldDisplayOptions) {
          prev.set('options', '1');
        }

        return prev;
      });
    }
  }

  /**
   * Add the cartId to the quersytring once created.
   */
  componentDidUpdate(prevProps: Props) {
    // Focus the new input when toggling bulk/single search mode.
    // FIXME: set focus at the end of the input?
    if (this.props.bulk !== prevProps.bulk && this.input.current) {
      this.input.current.focus();
    }

    // Push a new entry in the browser history for every new search.
    if (
      this.props.value !== prevProps.value &&
      this.props.value !== prevProps.searchParams.get('search')
    ) {
      this.props.setSearchParams(prev => {
        if (this.props.value) {
          prev.set('search', this.props.value);
        }
        if (this.props.bulk) {
          prev.set('bulk', '1');
        }

        return prev;
      });
    }

    // if the ?search param is updated but does not match state:
    // - Update the input's value.
    // - Re-launch a search if we have a value to search for.
    // Ex: making two searches in a row, then hitting back button.
    const currentSearch = this.props.searchParams.get('search');
    // TODO: [RR6] I'm pretty sure that searchParams are mutable so prevProps doesn't make sense at all
    // This probably needs to be migrated to FC with useEffect to make sure condition is correctly handled
    const prevSearch = prevProps.searchParams.get('search');
    if (
      currentSearch &&
      currentSearch !== prevSearch &&
      currentSearch !== this.props.value
    ) {
      if (this.input.current) {
        this.input.current.value = currentSearch;
        if (currentSearch) {
          this.handleSubmit();
        }
      }
    }

    // Update cartId param in location as necessary.
    // Cart may have been:
    // - created (adding 1st product, add cartId param to pathname)
    // - deleted (removing last product, remove cartId param from pathname).
    if ((this.props.params.cartId || '') !== this.props.cartId) {
      const newPathname = this.props.params.cartId
        ? this.props.location.pathname.replace(this.props.params.cartId, '')
        : superpathjoin(this.props.location.pathname, this.props.cartId);

      this.props.navigate(
        {
          pathname: newPathname,
          search: this.props.searchParams.toString(),
        },
        { replace: true },
      );
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    const {
      location: { query },
      loading,
      value,
      cartId,
      title,
      bulk,
      options,
    } = this.props;
    const { bulkRows } = this.state;

    if (loading !== nextProps.loading) {
      return true;
    }

    if (value !== nextProps.value) {
      return true;
    }

    if (bulk !== nextProps.bulk) {
      return true;
    }

    if (options !== nextProps.options) {
      return true;
    }

    if (bulkRows !== nextState.bulkRows) {
      return true;
    }

    if (query !== nextProps.location.query) {
      return true;
    }

    if (cartId !== nextProps.cartId) {
      return true;
    }

    if (title !== nextProps.title) {
      return true;
    }

    return false;
  }

  /**
   * - Search domains when the form is HTMLInputElementsubmitted.
   * - Push the new search in the browser history.
   */
  handleSubmit: (SyntheticEvent<> | void) => void = evt => {
    const { onSearchSubmit } = this.props;

    if (evt) evt.preventDefault();

    const input = this.input.current;
    if (input) {
      input.blur();
      const value = input.value;

      if (value) {
        this.props.submitSearch(value);
      }
    }
    if (typeof onSearchSubmit === 'function') {
      onSearchSubmit();
    }
  };

  toggleBulk: (evt: SyntheticMouseEvent<>) => void = (
    evt: SyntheticMouseEvent<>,
  ) => {
    if (evt.metaKey || evt.ctrlKey || evt.button === 1) return;

    // save input value before re-render.
    const input = this.input.current;
    const value = input ? input.value : '';

    this.setState({
      value,
    });
  };

  // KeyUp event handler used to submit the bulk search form on CTRL + ENTER
  handleTextareaKeyUp: (evt: SyntheticKeyboardEvent<>) => void = (
    evt: SyntheticKeyboardEvent<>,
  ) => {
    if (evt.key === 'Enter' && (evt.ctrlKey || evt.metaKey)) {
      this.handleSubmit();
    }
  };

  handleTextareaChange: (evt: SyntheticInputEvent<>) => void = (
    evt: SyntheticInputEvent<>,
  ) => {
    const target = evt.target;
    const value = target.value;
    const lineBreakSplittedValue = value.split('\n');
    const nbLineBreaks = lineBreakSplittedValue.length;

    const threshold = bulkRowsDefaultNb - 1;

    if (threshold - nbLineBreaks <= 1)
      this.setState({ bulkRows: nbLineBreaks + 1 });

    if (
      !target.classList.contains(styles.textareaLongSearch) &&
      lineBreakSplittedValue[0].length > 25
    ) {
      target.classList.add(styles.textareaLongSearch);
    } else if (
      target.classList.contains(styles.textareaLongSearch) &&
      lineBreakSplittedValue[0].length < 25
    ) {
      target.classList.remove(styles.textareaLongSearch);
    }
  };

  renderInput: () => React.MixedElement = () => {
    const domainCreate = this.props.process === 'create';
    const searchTerms = (this.state.value || this.props.value).replace(
      /\s+/g,
      ' ',
    );

    return (
      <div className={styles.inputContainer}>
        <div className={styles.dropShadow}>
          <input
            id={this.props.id}
            name={this.props.id}
            ref={this.input}
            className={styles.input}
            type="text"
            defaultValue={searchTerms}
            placeholder={
              this.props.placeholder ||
              this.props.translator.__('Enter one word or a domain name')
            }
            aria-label={this.props.label}
          />
          <div className={styles.toggleContainer}>
            {!this.props.hideBulk && this.renderBulkLink()}
            {domainCreate &&
              !this.props.hideOptions &&
              this.renderOptionsLink()}
            {this.renderButtonOrSpinner()}
          </div>
        </div>
      </div>
    );
  };

  renderTextarea: () => React.MixedElement = () => {
    const searchTerms = (this.state.value || this.props.value).replace(
      /\s+/g,
      '\n',
    );

    const domainCreate = this.props.process === 'create';

    return (
      <div className={styles.inputContainer}>
        <div className={styles.dropShadow}>
          <textarea
            id={this.props.id}
            name={this.props.id}
            ref={this.input}
            className={clsx(styles.input, styles.textarea)}
            rows={this.state.bulkRows}
            defaultValue={searchTerms}
            onKeyUp={this.handleTextareaKeyUp}
            onChange={this.handleTextareaChange}
            placeholder={this.props.translator.__(
              'Enter mutliple domains, one per line',
            )}
          />
          <div className={styles.toggleContainer}>
            {!this.props.hideBulk && this.renderBulkLink()}
            {domainCreate &&
              !this.props.hideOptions &&
              this.renderOptionsLink()}
            {this.renderButtonOrSpinner()}
          </div>
        </div>
      </div>
    );
  };

  renderBulkLink: () => React.Node = () => {
    const {
      bulk,
      translator: { __ },
      query,
    } = this.props;

    const nextQuery = {
      ...query,
      bulk: bulk ? undefined : 1,
    };

    const to = {
      pathname: this.props.location.pathname,
      search: serializeQuery(nextQuery),
    };

    const title = bulk ? __('Single search') : __('Multiple search');

    return (
      <Link
        to={to}
        title={title}
        className={clsx(
          styles.bulkToggle,
          bulk ? styles.toggleActive : undefined,
        )}
        onClick={this.toggleBulk}
      >
        <Svg theme="green" name="plus" className={styles.icon} />
      </Link>
    );
  };

  renderOptionsLink: () => React.Node = () => {
    const {
      options,
      translator: { __ },
      query,
    } = this.props;

    const nextQuery = {
      ...query,
      options: options ? undefined : 1,
    };

    const to: To = {
      pathname: this.props.location.pathname,
      search: serializeQuery(nextQuery),
    };

    const title = __('Search options');

    return (
      <Link
        to={to}
        title={title}
        className={clsx(
          styles.bulkToggle,
          options ? styles.toggleActive : undefined,
        )}
      >
        <Svg theme="green" name="options" className={styles.icon} />
      </Link>
    );
  };

  renderButtonOrSpinner: () => React.MixedElement = () => {
    const { bulk, translator, loading } = this.props;

    return (
      <IconButton
        loading={loading}
        className={clsx(styles.button, bulk ? styles.toggleOpen : undefined)}
        type="submit"
        icon="magnifying"
      >
        {translator.__('Search')}
      </IconButton>
    );
  };

  render(): React.Node {
    return (
      <Base marginBottom="base" className={styles.root}>
        {this.props.title}
        <form className={styles.form} onSubmit={this.handleSubmit} method="get">
          {this.props.bulk ? this.renderTextarea() : this.renderInput()}
          {this.props.children || null}
        </form>
      </Base>
    );
  }
}

const mapStateToProps = (
  state: GetState,
  props: WithRouterProps<
    {},
    {
      bulk: string,
      options: any,
    },
  >,
) => ({
  bulk: props.location.query.bulk === '1',
  options: props.location.query.options === '1',
  // $FlowFixMe[prop-missing]
  cartId: selectCartUUID(state),
});

export default (compose(
  withTranslator({ propsNamespace: 'translator' }),
  withRouter,
  connect(mapStateToProps),
)(DomainInput): React.ComponentType<Omit<Props, 'translator'>>);
